import { useAtom } from '@reatom/npm-react'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { useTranslation } from '@root/shared/localization'
import { FadeOverflow, InfoMessage, mediaMaxTablet } from '@root/shared/ui-kit'

import { supportDialogScreenAtom } from '../atoms'
import { supportTicketsModel } from '../atoms/reatom-support-ticket'

import { SupportTicketsItem } from './support-tickets-item'
import { SupportWelcome } from './support-welcome'

import type { SupportTicket } from '../model/support-ticket'

export const SupportTickets: React.FC<{ onClose?: () => void }> = ({
  onClose,
}) => {
  const { t } = useTranslation()
  const [ticketsState] = useAtom(supportTicketsModel)
  const [, setScreen] = useAtom(supportDialogScreenAtom)

  const renderEmptyMessage = () => (
    <RootEmpty>
      <SupportWelcome onClose={onClose} />
    </RootEmpty>
  )

  return AsyncStateN.match(ticketsState, {
    NotInitiated: renderEmptyMessage,
    Loading: () => (
      <RootEmpty>
        <SupportWelcome onClose={onClose} busy />
      </RootEmpty>
    ),
    Success: ({ data: tickets }) => {
      if (tickets.length === 0) {
        return renderEmptyMessage()
      }

      return (
        <Root>
          <SupportWelcomeTop onClose={onClose} />
          <TicketsList>
            <Background>
              <Heading>{t('support.past-tickets')}</Heading>
              <FadeOverflowList
                mode="always"
                variant="vertical"
                blockStart={6}
                blockEnd={16}
              >
                {tickets.map((ticket: SupportTicket) => (
                  <SupportTicketsItem
                    key={ticket.id}
                    ticket={ticket}
                    onSelect={() => {
                      setScreen({
                        screen: 'conversation',
                        ticket,
                      })
                    }}
                  />
                ))}
              </FadeOverflowList>
            </Background>
          </TicketsList>
        </Root>
      )
    },
    Error: () => (
      <RootEmpty>
        <InfoMessage intent="error" withBackground onRetry={() => {}} />
      </RootEmpty>
    ),
  })
}

const Root = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
`

const RootEmpty = styled(Root)`
  height: auto;
`

const SupportWelcomeTop = styled(SupportWelcome)`
  padding-block-end: 18px;
`

const TicketsList = styled.div`
  padding: 3px;
`

const FadeOverflowList = styled(FadeOverflow)`
  max-height: 200px;
  overflow-y: auto;
  align-content: start;

  display: grid;
  gap: 3px;
  padding-inline-end: 6px;
  padding-block-end: 6px;

  ${mediaMaxTablet(css`
    max-height: 280px;
  `)}
`

const Background = styled.div`
  background: var(--appearanceA-5);
  border-radius: 10px;
  padding: 10px;
  height: 100%;
  width: 100%;
  padding-inline-end: 0;
  padding-block-end: 0;
`

const Heading = styled.div`
  color: var(--text-11);

  font-size: 11px;
  font-weight: 500;
  line-height: 16px;

  margin-bottom: 6px;
`
